/**
 * Aufrufe der Datenschutzwerkzeuge -- F5-11 bis F5-15.
 *
 * **Die Ausgaben laufen ueber `fetch` und nicht ueber einen Link.** Ein
 * `<a href>` traegt den `Authorization`-Kopf nicht mit; ein Merkmal in der
 * Adresszeile stuende im Verlauf des Browsers. Bei einer Auskunft nach
 * Art. 15 waere das besonders unangenehm: Die Datei enthaelt alles, was ueber
 * eine Person gespeichert ist.
 */

import type { components } from '../../api/schema';
import { download as fetchFile, request } from '../../lib/apiClient';

export type DataSubjectRequest = components['schemas']['DataSubjectRequestRead'];
export type RequestKind = components['schemas']['RequestKind'];
export type RequestStatus = components['schemas']['RequestStatus'];
export type RetentionJob = components['schemas']['RetentionJobRead'];
export type RetentionRunResult = components['schemas']['RetentionRunResult'];
export type PersonalDataTable = components['schemas']['PersonalDataTable'];

export function listRequests(openOnly: boolean): Promise<DataSubjectRequest[]> {
  return request<DataSubjectRequest[]>('/privacy/requests', {
    query: openOnly ? { open_only: true } : {},
  });
}

export function createRequest(payload: Record<string, unknown>): Promise<DataSubjectRequest> {
  return request<DataSubjectRequest>('/privacy/requests', { method: 'POST', body: payload });
}

export function updateRequest(
  requestId: string,
  payload: Record<string, unknown>,
): Promise<DataSubjectRequest> {
  return request<DataSubjectRequest>(`/privacy/requests/${requestId}`, {
    method: 'PATCH',
    body: payload,
  });
}

export function listRetentionJobs(): Promise<RetentionJob[]> {
  return request<RetentionJob[]>('/privacy/retention-jobs');
}

export function runRetention(dryRun: boolean): Promise<RetentionRunResult> {
  return request<RetentionRunResult>('/privacy/retention-jobs/actions/run', {
    method: 'POST',
    body: { dry_run: dryRun },
  });
}

export function listPersonalData(): Promise<PersonalDataTable[]> {
  return request<PersonalDataTable[]>('/privacy/personal-data');
}

/**
 * Holt eine Datei mit dem Anmeldemerkmal im Kopf -- siehe Modulkopf.
 *
 * Ueber den gemeinsamen Abruf, damit ein Fehler als ``ApiError`` mit
 * Fehlerkennung ankommt und nicht als nackte Statusnummer.
 */
async function download(path: string, query: Record<string, string> = {}): Promise<Blob> {
  const params = new URLSearchParams();
  for (const [name, value] of Object.entries(query)) {
    if (value !== '') params.set(name, value);
  }
  const search = params.toString();
  const { blob } = await fetchFile(search ? `${path}?${search}` : path);
  return blob;
}

export function downloadSubjectAccess(contactId: string, format: 'json' | 'pdf'): Promise<Blob> {
  return download(
    format === 'pdf'
      ? `/privacy/subject-access/${contactId}/export.pdf`
      : `/privacy/subject-access/${contactId}`,
  );
}

/** Legt eine geholte Datei im Download-Ordner ab. */
export function save(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
